// Toggling FixedColumns' trailing frozen column, safely.
//
// FixedColumns 5.0.1 registers its API like this:
//
//   apiRegister('fixedColumns()',       function () { return this; });
//   apiRegister('fixedColumns().end()', function (newVal) {
//     var ctx = this.context[0];
//     ctx._fixedColumns.end(newVal);
//   });
//
// Two consequences, both of which cost us a release to learn:
//
// 1. `fixedColumns()` returns the API object unconditionally, and `.end` is
//    always a registered function. Optional chaining across that call therefore
//    guards nothing -- ENG-591 did exactly that and did not move the error.
// 2. `.end()` dereferences `ctx._fixedColumns` with no check of its own. That
//    property only exists once the extension has constructed against the table,
//    so calling .end() before then throws. It shows up as three different
//    browser wordings of one dereference: "Cannot read properties of undefined
//    (reading 'end')" in Chrome, "undefined is not an object (evaluating
//    'e._fixedColumns.end')" in Safari, and "can't access property \"end\",
//    e._fixedColumns is undefined" in Firefox.
//
// So the only meaningful guard is on `_fixedColumns` itself, which is what the
// library dereferences. Reaching for an internal is deliberate: the public API
// offers no readiness signal, and checking the exact property the library will
// dereference is more honest than a try/catch that would also swallow real
// failures inside the extension.

interface FixedColumnsCapableTable {
  settings?: () => Array<{ _fixedColumns?: unknown } | undefined> | undefined;
  fixedColumns?: () => { end?: (value: number) => void } | undefined;
}

// Has the extension constructed against this table yet?
export function fixedColumnsAttached(
  datatable: FixedColumnsCapableTable | null | undefined
): boolean {
  try {
    return Boolean(datatable?.settings?.()?.[0]?._fixedColumns);
  } catch {
    // A destroyed or half-initialised table can throw from settings(); that is
    // simply "not ready" rather than something worth propagating.
    return false;
  }
}

// Returns whether the value was applied, so callers can retry rather than
// silently dropping the toggle.
export function applyFixedColumnsEnd(
  datatable: FixedColumnsCapableTable | null | undefined,
  value: number | boolean
): boolean {
  if (!fixedColumnsAttached(datatable)) return false;

  datatable!.fixedColumns!()!.end!(Number(value));
  return true;
}
